import {
  Checkbox,
  ClickAwayListener,
  Grid,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  makeStyles
} from "@material-ui/core";
import * as React from "react";
import { Virtuoso } from "react-virtuoso";
import { IconRemove } from "src/common/icons";
import { PopperModifiers } from "./Popper.config";

const useStyles = makeStyles((theme: any) => ({
  checkbox: {
    padding: "0 16px"
  },
  container: {
    '& div[role="tooltip"]': {
      zIndex: 3
    },
    background: "#ffffff",
    border: "1px solid #bdbdbd",
    borderRadius: 4,
    padding: "14px 0 0px",
    position: "relative" as "relative",
    [theme.breakpoints.up("md")]: {
      background: "none" as "none",
      border: "none" as "none",
      borderRadius: 0,
      padding: "0"
    }
  },
  containerOpen: {},
  empty: {
    "& $label": {
      color: "#7e7e7e",
      fontSize: "1.4rem",
      top: "50%",
      transform: "translateY(-50%)",
      transition: "all .2s ease-in"
    },
    "&:focus-within $label": {
      color: "#545454",
      fontSize: "1rem",
      top: 0,
      transform: "translateY(0%)",
      transition: "all .2s ease-out",
      [theme.breakpoints.up("md")]: {
        fontSize: "1.4rem"
      }
    }
  },
  input: {
    border: "none" as "none",
    fontSize: "1.6rem",
    minWidth: 75,
    textOverflow: "ellipsis" as "ellipsis",
    width: "100%"
  },
  inputContainer: {
    alignItems: "center" as "center",
    display: "flex" as "flex",
    height: 36,
    lineHeight: "34px",
    maxWidth: "100%",
    overflow: "scroll",
    padding: "0 8px",
    [theme.breakpoints.up("md")]: {
      background: "#ffffff",
      border: "1px solid #bdbdbd",
      borderRadius: 4
    }
  },
  label: {
    color: "#545454",
    fontSize: "1rem",
    padding: "0 8px",
    pointerEvents: "none" as "none",
    position: "absolute" as "absolute",
    top: "0%",
    [theme.breakpoints.up("md")]: {
      fontSize: "1.4rem",
      padding: "0",
      position: "static" as "static"
    }
  },
  longListOptions: {
    height: "200px !important",
    [theme.breakpoints.up("md")]: {
      height: "380px !important"
    }
  },
  menuItem: {
    fontSize: "1.4rem",
    height: "auto" as "auto"
  },
  optionLabel: {
    display: "inline-block" as "inline-block",
    fontSize: "1.4rem",
    lineHeight: 1,
    whiteSpace: "initial" as "initial"
  },
  popperContainer: {
    maxHeight: "200px",
    overflow: "auto" as "auto",
    [theme.breakpoints.up("md")]: {
      maxHeight: "380px"
    }
  },
  tag: {
    alignItems: "center" as "center",
    background: theme.palette.primary.main,
    borderRadius: 3,
    color: "#fff",
    cursor: "pointer" as "pointer",
    display: "flex" as "flex",
    fontSize: "1.3rem",
    height: 24,
    lineHeight: "24px",
    marginRight: 4,
    padding: "0 4px",
    whiteSpace: "nowrap" as "nowrap"
  },
  tagRemoveBtn: {
    '& [class*="icon-"]': {
      "&:hover": {
        opacity: 1
      },
      color: "#fff",
      fontSize: "1.2rem",
      marginLeft: 2,
      opacity: 0.54
    },
    background: "none" as "none",
    border: "none" as "none",
    display: "inline-block" as "inline-block"
  }
}));

const STANDARD_LENGTH = 100;

interface IOption {
  value: any;
  label: string;
}

interface IOwnProps {
  options: IOption[];
  defaultValues?: any[];
  defaultOpen?: boolean;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  singleSelection?: boolean; // when true only one option per selection is allowed
  onChange?: (arg0: any) => void;
}

const Autocomplete = ({
  defaultValues = [],
  disabled,
  label,
  options,
  onChange,
  placeholder,
  singleSelection = false
}: IOwnProps) => {
  const classes = useStyles();

  const [searchText, setSearchText] = React.useState("");
  const [values, setValues] = React.useState(defaultValues);
  const [menuOpen, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [focusedElem, setFocusedElem] = React.useState<null | number>(null);
  const focusedElemRef = React.useRef<any>(null);

  const containerClasses = [classes.container];

  if (menuOpen) {
    containerClasses.push(classes.containerOpen);
  }

  React.useEffect(() => {
    const allOptionsValues = options.map(({ value }) => value);
    const valueExcluded = values.some(value => !allOptionsValues.includes(value));
    if (valueExcluded) {
      const newValues = values.filter(value => allOptionsValues.includes(value));

      if (onChange) {
        onChange(newValues);
      }
      setValues(newValues);
    }
  }, [{ options }]);

  React.useEffect(() => {
    setValues(defaultValues);
  }, [defaultValues]);

  const handleTextSearch = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setSearchText(value);
  }, []);

  const handleClickAway = React.useCallback((event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
    setSearchText("");
  }, []);

  const handleInputFocus = React.useCallback(
    e => {
      if (!menuOpen) {
        setOpen(true);
      }
    },
    [menuOpen]
  );

  const scrollContainer = React.useCallback(() => {
    if (containerRef.current) {
      const { scrollWidth } = containerRef.current;
      containerRef.current.scrollLeft = scrollWidth;
    }
  }, [containerRef]);

  const updateValues = React.useCallback(
    (newValues: any) => {
      if (menuOpen && inputRef.current) {
        inputRef.current.focus();
      }
      setValues(newValues);
      if (onChange) {
        onChange(newValues);
      }
      setTimeout(() => {
        scrollContainer();
      });
    },
    [scrollContainer, menuOpen, onChange]
  );

  const getTags = React.useCallback(() => {
    const allOptionValues = options.map(({ value }) => value);
    return values
      .filter(value => allOptionValues.includes(value))
      .map(tabValue => {
        return {
          label: (options.find(({ value: optionValue }) => tabValue === optionValue) as IOption).label,
          value: tabValue
        };
      });
  }, [values, options]);

  const getVisibleOptions = React.useCallback(() => {
    return options.filter(({ label: optionLabel }) => optionLabel.toLowerCase().includes(searchText.toLowerCase()));
  }, [values, options, searchText]);

  const handleContainerClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!menuOpen && inputRef.current) {
        inputRef.current.focus();
      }
    },
    [inputRef, menuOpen]
  );

  const visibleOptions = getVisibleOptions();

  React.useEffect(() => {
    if (menuOpen) {
      window.dispatchEvent(new Event("resize"));
    }
  }, [{ visibleOptions }]);

  const handleArrows = React.useCallback(
    (e: KeyboardEvent) => {
      const { keyCode } = e;

      switch (keyCode) {
        // BACKSPACE
        case 8:
          if (!searchText.length && values.length) {
            e.preventDefault();
            updateValues(values.slice(0, values.length - 1));
          }
          break;

        // UP
        case 38:
          e.preventDefault();
          setFocusedElem(focusedElem === null ? visibleOptions.length - 1 : focusedElem - 1);
          break;

        // DOWN
        case 40:
          e.preventDefault();
          setFocusedElem(focusedElem === null ? 0 : focusedElem + 1);
          break;

        // ENTER
        case 13:
          e.preventDefault();
          if (focusedElemRef.current) {
            focusedElemRef.current.click();
          }
      }
    },
    [visibleOptions, focusedElem, focusedElemRef.current, searchText, updateValues, values]
  );

  React.useEffect(() => {
    if (menuOpen) {
      document.addEventListener("keydown", handleArrows);
    } else {
      document.removeEventListener("keydown", handleArrows);
      setFocusedElem(null);
    }

    return () => {
      document.removeEventListener("keydown", handleArrows);
    };
  }, [menuOpen, handleArrows, focusedElemRef]);

  React.useEffect(() => {
    if (menuOpen) {
      setFocusedElem(null);
    }
  }, [visibleOptions.length, menuOpen]);

  if (!getTags().length) {
    containerClasses.push(classes.empty);
  }

  const renderMenuItems = (optionValue: string, optionLabel: string, key: number) => {
    const isChecked = singleSelection
      ? // prohibit user from selecting more than one values if singleSelection is true
        values.length <= 1 && values.includes(optionValue)
      : values.includes(optionValue);

    const handleClick = () => {
      setSearchText("");
      if (isChecked) {
        updateValues(values.filter(checkedValue => checkedValue !== optionValue));
      } else {
        singleSelection ? updateValues([optionValue]) : updateValues([...values, optionValue]);
      }
    };

    const selected = key === focusedElem;

    return (
      <MenuItem className={classes.menuItem} selected={selected} key={key} onClick={handleClick}>
        <Grid container justifyContent="space-between" wrap="nowrap" alignItems="center">
          <span ref={selected ? focusedElemRef : undefined} className={classes.optionLabel}>
            {optionLabel}
          </span>
          <Checkbox className={classes.checkbox} checked={isChecked} />
        </Grid>
      </MenuItem>
    );
  };

  // tslint:disable-next-line
  const checkIfOptionsPresent = (options: IOption[]) => {
    if (!options.length) {
      return <MenuItem className={classes.menuItem}>No results</MenuItem>;
    }
    return null;
  };

  return (
    <div ref={anchorRef} className={containerClasses.join(" ")}>
      {label ? <label className={classes.label}>{label}</label> : null}
      <div
        onClick={handleContainerClick}
        ref={containerRef}
        className={classes.inputContainer}
        style={disabled ? { background: "#f0f0f0" } : {}}
      >
        {getTags().map(({ value: optionValue, label: optionLabel }) => {
          const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            updateValues(values.filter(value => value !== optionValue));
          };
          return (
            <div className={classes.tag} key={optionValue}>
              <span>{optionLabel}</span>
              <button type="button" onClick={handleClick} className={classes.tagRemoveBtn}>
                <IconRemove />
              </button>
            </div>
          );
        })}
        <input
          value={searchText}
          ref={inputRef}
          onChange={handleTextSearch}
          onFocus={handleInputFocus}
          className={classes.input}
          placeholder={placeholder}
          type="text"
          disabled={disabled}
        />
      </div>
      <Popper modifiers={PopperModifiers} transition disablePortal anchorEl={anchorRef.current} open={menuOpen}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom" ? "center top" : "center bottom"
            }}
          >
            <Paper className={classes.popperContainer}>
              <ClickAwayListener onClickAway={handleClickAway}>
                <MenuList>
                  {checkIfOptionsPresent(visibleOptions)}
                  {visibleOptions.length < STANDARD_LENGTH ? (
                    visibleOptions.map(({ value: optionValue, label: optionLabel }, key) =>
                      renderMenuItems(optionValue, optionLabel, key)
                    )
                  ) : (
                    <Virtuoso
                      className={classes.longListOptions}
                      totalCount={visibleOptions.length}
                      item={index => {
                        const { value: optionValue, label: optionLabel } = visibleOptions[index];
                        return renderMenuItems(optionValue, optionLabel, index);
                      }}
                    />
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default Autocomplete;
